"use client";
import { Card, CardBody, CardFooter, Skeleton, Button, Image } from '@heroui/react'
import { truncAddress } from '@/util/address'
import Link from 'next/link'
import { Icon } from '@iconify/react'
import { useRouter } from 'next/navigation'
import { getCollectionByAddress } from '@/constants/marketplace/collections'
import { chainSettings, type ChainType, ChainId, defaultNativeAddress } from '@/constants/chain-specific'
import { getTokensByChainId, getNativeCurrency } from '@/constants/marketplace/tokens'
import { type Address } from 'viem'
import { Format } from '../Format'
import { getOptimizedImageUrl } from '@/util/ipfs'

interface NFTCardProps {
  nft: {
    id: string
    name: string
    description: string
    image: string
    owner: string
    tokenId: string
    assetContractAddress: string
    collectionName: string
    chainId: number
    symbol?: string
    listing?: {
      formattedPrice: string
      currencySymbol: string
      currencyAddress?: Address
      currencyLogo?: string
      endsAt?: string
    }
    auction?: {
      formattedMinimumBid: string
      formattedHighestBid: string
      currencySymbol: string
      currencyAddress?: Address
      currencyLogo?: string
      endTimeInSeconds: number
    }
    rarityRank?: number
    maxSupply?: number
  }
  isLoading?: boolean
}

// Helper function to determine rarity tier and color
function getRarityTier(rank: number, maxSupply: number) {
  if (maxSupply === 0) return { tier: 'Unknown', color: 'text-gray-400' }

  const percentage = (rank / maxSupply) * 100

  if (percentage <= 1) return { tier: 'Legendary', color: 'text-orange-400' }
  if (percentage <= 5) return { tier: 'Epic', color: 'text-purple-400' }
  if (percentage <= 15) return { tier: 'Rare', color: 'text-blue-400' }
  if (percentage <= 30) return { tier: 'Uncommon', color: 'text-green-400' }
  return { tier: 'Common', color: 'text-gray-400' }
}

// Helper function to format auction end time
function formatEndTime(endTimeInSeconds: number): string {
  const now = Math.floor(Date.now() / 1000)
  const timeLeft = endTimeInSeconds - now

  if (timeLeft <= 0) return 'Ended'

  const days = Math.floor(timeLeft / 86400)
  const hours = Math.floor((timeLeft % 86400) / 3600)
  const minutes = Math.floor((timeLeft % 3600) / 60)

  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}


export default function NFTCard({ nft, isLoading = false }: NFTCardProps) {
  const router = useRouter()

  // Ensure chainId is defined, fallback to Shibarium if not
  const chainId = nft.chainId || ChainId.SHIBARIUM

  const collection = getCollectionByAddress(nft.assetContractAddress, chainId)

  // Check if this is a trading card collection to prevent image cropping
  const isTradingCard = collection?.isTradingCard || false

  // Handle collection name click
  const handleCollectionClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const chainSetting = chainSettings[chainId as ChainType]
    if (!chainSetting) {
      console.error('Chain settings not found for chain ID:', chainId)
      return
    }

    // Use slug-based URL if available, otherwise fallback to address
    const collectionUrl = collection?.slug
      ? `/marketplace/collection/${chainSetting.chainSlug}/${collection.slug}`
      : `/marketplace/collection/${chainSetting.chainSlug}/${nft.assetContractAddress}`
    router.push(collectionUrl)
  }

  // Generate asset URL with slug if available
  const chainSetting = chainSettings[chainId as ChainType]
  if (!chainSetting) {
    console.error('Chain settings not found for chain ID:', chainId)
    // Use fallback URL with shibarium as default
    const fallbackChainSlug = 'shibarium'
    const assetUrl = collection?.slug
      ? `/marketplace/${fallbackChainSlug}/${collection.slug}/${nft.tokenId}`
      : `/marketplace/${fallbackChainSlug}/${nft.assetContractAddress}/${nft.tokenId}`

    return (
      <Card className="overflow-hidden transition-all hover:shadow-md border border-gray-200 dark:border-gray-800">
        <Link href={assetUrl}>
          <div className="relative aspect-square w-full overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <span className="text-gray-500">Invalid Chain ID</span>
            </div>
          </div>
          <CardBody>
            <h3 className="font-medium truncate">
              {nft.name || `#${nft.tokenId}`}
            </h3>
            <div className="text-sm text-gray-500">
              {nft.collectionName}
            </div>
          </CardBody>
        </Link>
      </Card>
    )
  }

  const assetUrl = collection?.slug
    ? `/marketplace/${chainSetting.chainSlug}/${collection.slug}/${nft.tokenId}`
    : `/marketplace/${chainSetting.chainSlug}/${nft.assetContractAddress}/${nft.tokenId}`

  if (isLoading) {
    return (
      <Card className="overflow-hidden border border-gray-200 dark:border-gray-800">
        <div className="relative aspect-square w-full">
          <Skeleton className="h-full w-full" />
        </div>
        <CardBody>
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="mt-2 h-4 w-1/2" />
        </CardBody>
        <CardFooter className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
        </CardFooter>
      </Card>
    )
  }

  return (

    <Card className="overflow-hidden transition-all hover:shadow-md border border-gray-200 dark:border-gray-800">
      <Link href={assetUrl}>
        <div className="relative aspect-square w-full overflow-hidden">
          {/* Rarity Badge */}
          {nft.rarityRank && (
            <div className="flex flex-row absolute top-2 right-2 z-10 bg-slate-900/50 rounded-lg px-3 py-1 text-xs font-bold">
              <Icon icon="material-symbols:trophy" className={getRarityTier(nft.rarityRank, nft.maxSupply || 0).color} width="15" height="15" /> {nft.maxSupply ? nft.rarityRank : nft.rarityRank}
            </div>
          )}

          {nft.image && (
            <Image
              src={getOptimizedImageUrl(nft.image, 256, 256, 85, 'auto', isTradingCard ? 'contain' : 'cover')}
              alt={nft.name || 'NFT'}
              loading="lazy"
              width={"100%"}
              className={`transition-transform hover:scale-105 ${isTradingCard ? 'object-contain' : 'object-cover'
                }`}
            />
          )}
        </div>
        <CardBody>
          <h3 className="font-medium truncate">
            {nft.name
              ? (nft.name.length > 20 && nft.symbol
                ? `${nft.symbol} #${nft.tokenId}`
                : nft.name)
              : `#${nft.tokenId}`
            }
          </h3>
          <div className="flex items-center">
            <span
              className="text-sm hover:underline truncate cursor-pointer"
              onClick={handleCollectionClick}
            >
              {nft.collectionName}
            </span>
          </div>
          {nft.owner && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Owned by {truncAddress(nft.owner)}
            </div>
          )}
        </CardBody>
        <CardFooter className="flex flex-col gap-2">
          {nft.listing && (
            <>
              <div className="flex items-center justify-between">

                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {(() => {
                    return nft.listing?.currencyLogo ? (
                      <Image
                        src={nft.listing.currencyLogo}
                        alt={nft.listing.currencySymbol || ''}
                        width={20}
                        height={20}
                        className="rounded-full flex-shrink-0"
                      />
                    ) : null
                  })()}
                  <span
                    className="font-bold whitespace-nowrap overflow-hidden min-w-0"
                    style={{
                      fontSize: 'clamp(0.75rem, 2.5vw, 1.125rem)',
                      transform: 'scale(1)',
                      transformOrigin: 'left center'
                    }}
                  >
                    <Format.Number value={Number(nft.listing.formattedPrice)} /> {nft.listing.currencySymbol}
                  </span>
                </div>
              </div>
              {nft.listing.endsAt && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium pr-1">Ends</span>
                  <span className="text-xs">{nft.listing.endsAt}</span>
                </div>
              )}
              <Button
                size="sm"
                variant="solid"
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                onPress={() => { }} // Placeholder for future functionality
              >
                View Details
              </Button>
            </>
          )}

          {nft.auction && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium pr-1">Min Bid:</span>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {(() => {
                    return nft.auction?.currencyLogo ? (
                      <Image
                        src={nft.auction.currencyLogo}
                        alt={nft.auction.currencySymbol || ''}
                        width={14}
                        height={14}
                        className="rounded-full flex-shrink-0"
                      />
                    ) : null
                  })()}
                  <span
                    className="font-bold whitespace-nowrap overflow-hidden min-w-0"
                    style={{
                      fontSize: 'clamp(0.625rem, 2vw, 0.875rem)',
                      transform: 'scale(1)',
                      transformOrigin: 'left center'
                    }}
                  >
                    {nft.auction.formattedMinimumBid} {nft.auction.currencySymbol}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium pr-1">Current:</span>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  {(() => {
                    return nft.auction?.currencyLogo ? (
                      <Image
                        src={nft.auction.currencyLogo}
                        alt={nft.auction.currencySymbol || ''}
                        width={14}
                        height={14}
                        className="rounded-full flex-shrink-0"
                      />
                    ) : null
                  })()}
                  <span
                    className="font-bold whitespace-nowrap overflow-hidden min-w-0"
                    style={{
                      fontSize: 'clamp(0.625rem, 2vw, 0.875rem)',
                      transform: 'scale(1)',
                      transformOrigin: 'left center'
                    }}
                  >
                    {nft.auction.formattedHighestBid} {nft.auction.currencySymbol}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium pr-1">Ends in</span>
                <span className="text-xs">{formatEndTime(nft.auction.endTimeInSeconds)}</span>
              </div>
              <Button
                size="sm"
                variant="solid"
                className="w-full mt-2 bg-purple-600 hover:bg-purple-700"
                onPress={() => { }} // Placeholder for future functionality
              >
                View Auction
              </Button>
            </>
          )}

          {/* Show default view details button if no listing or auction */}
          {!nft.listing && !nft.auction && (
            <Button
              size="sm"
              variant="bordered"
              className="w-full mt-2"
              onPress={() => { }} // Placeholder for future functionality
            >
              View NFT
            </Button>
          )}
        </CardFooter>
      </Link>
    </Card>
  )
} 